// ✅ Helper functions untuk format data template DOCX

// ✅ ISCC SCOPE: Cache for ISCC scope options to avoid repeated API calls
let isccScopeCache = null;
let isccScopeCachePromise = null;

// ✅ ISCC SCOPE: Fetch ISCC scope options from backend
const fetchIsccScopeOptions = async () => {
  // Return cached data if available
  if (isccScopeCache) {
    return isccScopeCache;
  }

  // Return pending promise if already fetching
  if (isccScopeCachePromise) {
    return isccScopeCachePromise;
  }

  // Start fetching
  isccScopeCachePromise = (async () => {
    try {
      // Import api dynamically to avoid circular dependencies
      const { default: api } = await import('../../../../api');

      // Try the main endpoint first - fetch all scopes without pagination
      let response;
      try {
        response = await api.get('/md/iscc-scope/all');
      } catch (endpointError) {
        // Fallback to paginated endpoint with large page size
        try {
          response = await api.get('/md/iscc-scope?page=0&size=1000');
        } catch (pageError) {
          // Try alternative endpoints
          const alternativeEndpoints = [
            '/api/md/iscc-scope/all',
            '/crm/iscc-scope/all',
            '/master/iscc-scope/all',
            '/md/scope/all',
            '/api/md/iscc-scope?page=0&size=1000',
            '/crm/iscc-scope?page=0&size=1000',
            '/master/iscc-scope?page=0&size=1000',
            '/md/scope?page=0&size=1000'
          ];

          for (const endpoint of alternativeEndpoints) {
            try {
              response = await api.get(endpoint);
              break;
            } catch (altError) {
              // Continue to next endpoint
            }
          }
        }
      }

      if (!response) {
        throw new Error('All endpoints failed');
      }

      // Handle different response structures
      let scopeData = [];
      if (response && response.data) {
        if (Array.isArray(response.data)) {
          // Direct array response from /all endpoint
          scopeData = response.data;
        } else if (response.data.content && Array.isArray(response.data.content)) {
          // Paginated response structure
          scopeData = response.data.content;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          scopeData = response.data.data;
        } else if (response.data.items && Array.isArray(response.data.items)) {
          scopeData = response.data.items;
        } else if (response.data.records && Array.isArray(response.data.records)) {
          scopeData = response.data.records;
        } else {
          scopeData = [];
        }
      }

      if (scopeData.length > 0) {
        // Create mapping from ID/value to name/code
        const scopeMapping = {};
        scopeData.forEach(scope => {
          const id = scope.id || scope.value || scope.code;
          const name = scope.name || scope.label || 'Unknown Scope';
          const code = scope.code || String(id);

          // Map by ID
          if (id) {
            scopeMapping[String(id)] = name;
            scopeMapping[String(id).toLowerCase()] = name;
          }

          // Map by code (for backward compatibility)
          if (code) {
            scopeMapping[String(code)] = name;
            scopeMapping[String(code).toLowerCase()] = name;
          }

          // Map common variations
          if (code && code.match(/scope\s*(\d+)/i)) {
            const scopeNum = code.match(/scope\s*(\d+)/i)[1];
            scopeMapping[`scope${scopeNum}`] = name;
            scopeMapping[scopeNum] = name;
          }
        });

        isccScopeCache = scopeMapping;
        return scopeMapping;
      } else {
        // Fallback mapping - include common ISCC scope IDs
        isccScopeCache = {
          '1': 'ISCC Scope 1',
          '2': 'ISCC Scope 2',
          '3': 'ISCC Scope 3',
          '25': 'ISCC Scope 1', // Add specific mappings for common IDs
          '27': 'ISCC Scope 2', // Add specific mappings for common IDs
          'scope1': 'ISCC Scope 1',
          'scope2': 'ISCC Scope 2',
          'scope3': 'ISCC Scope 3',
          'fallback-1': 'ISCC Scope 1 - Fallback Option',
          'fallback-2': 'ISCC Scope 2 - Fallback Option',
          'fallback-3': 'ISCC Scope 3 - Fallback Option'
        };
        return isccScopeCache;
      }
    } catch (error) {
      console.warn('Failed to fetch ISCC scope options:', error);
      // Fallback mapping - include common ISCC scope IDs
      isccScopeCache = {
        '1': 'ISCC Scope 1',
        '2': 'ISCC Scope 2',
        '3': 'ISCC Scope 3',
        '25': 'ISCC Scope 1', // Add specific mappings for common IDs
        '27': 'ISCC Scope 2', // Add specific mappings for common IDs
        'scope1': 'ISCC Scope 1',
        'scope2': 'ISCC Scope 2',
        'scope3': 'ISCC Scope 3',
        'fallback-1': 'ISCC Scope 1 - Fallback Option',
        'fallback-2': 'ISCC Scope 2 - Fallback Option',
        'fallback-3': 'ISCC Scope 3 - Fallback Option'
      };
      return isccScopeCache;
    }
  })();

  return isccScopeCachePromise;
};

// Format currency dengan format dinamis berdasarkan currency code
export const formatCurrency = (amount, currencyCode = 'IDR') => {
  
  if (!amount) {
    const result = getCurrencySymbol(currencyCode) + ' 0';
    return result;
  }
  
  const numAmount = parseFloat(amount);
  
  if (isNaN(numAmount)) {
    const result = getCurrencySymbol(currencyCode) + ' 0';
    return result;
  }
  
  const result = numAmount.toLocaleString('id-ID', { 
    style: 'currency', 
    currency: currencyCode,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });
  return result;
};

// Helper function untuk mendapatkan currency symbol
const getCurrencySymbol = (currencyCode) => {
  switch (currencyCode) {
    case 'USD': return '$';
    case 'EUR': return '€';
    case 'GBP': return '£';
    case 'JPY': return '¥';
    case 'SGD': return 'S$';
    case 'AUD': return 'A$';
    case 'IDR': return 'Rp';
    default: return 'Rp';
  }
};

// Format number dengan separator ribuan
export const formatNumberWithSeparator = (number) => {
  if (!number) return '0';
  
  const numValue = parseFloat(number);
  if (isNaN(numValue)) return '0';
  
  return numValue.toLocaleString('id-ID');
};

// Format decimal dengan 2 digit
export const formatDecimal = (number) => {
  if (!number) return '0,00';
  
  const numValue = parseFloat(number);
  if (isNaN(numValue)) return '0,00';
  
  return numValue.toLocaleString('id-ID', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

// Format percentage
export const formatPercentage = (number) => {
  if (!number) return '0%';
  
  const numValue = parseFloat(number);
  if (isNaN(numValue)) return '0%';
  
  return `${numValue.toFixed(2)}%`;
};

// Helper function to remove text after '#' character
export const removeTextAfterHash = (text) => {
  if (!text || typeof text !== 'string') return text;
  
  // Find the position of '#' character
  const hashIndex = text.indexOf('#');
  
  // If '#' found, return text before it (trimmed)
  if (hashIndex !== -1) {
    return text.substring(0, hashIndex).trim();
  }
  
  // If no '#' found, return original text
  return text;
};

// Fungsi utama untuk format data template
// ✅ CMF CHECKBOX helpers — used in both service_items and services.standards.service_items
const CMF_TIERS = ['basic', 'best practice', 'benchmark'];
// Unique BMP private-use sentinels — postProcessDocxCheckboxes replaces these with real FORMCHECKBOX XML.
// Using U+E001/E002 (Private Use Area) so they can never appear in normal document text.
const CMF_CHECKED   = ''; // PUA sentinel: checked
const CMF_UNCHECKED = ''; // PUA sentinel: unchecked

// Escape special XML chars in text values
const escXml = (s) => String(s || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');

// Real Word FORMCHECKBOX field run + label text run
// &#9746; = ☒ (checked display), &#9744; = ☐ (unchecked display)
const wordCheckboxLine = (checked, label, price) => {
  const displayChar = checked ? '&#9746;' : '&#9744;';
  const checkedXml  = checked ? '<w:checked/>' : '';
  return (
    `<w:r><w:fldChar w:fldCharType="begin"><w:ffData><w:checkBox><w:sizeAuto/>${checkedXml}</w:checkBox></w:ffData></w:fldChar></w:r>` +
    `<w:r><w:instrText xml:space="preserve"> FORMCHECKBOX </w:instrText></w:r>` +
    `<w:r><w:fldChar w:fldCharType="separate"/></w:r>` +
    `<w:r><w:t>${displayChar}</w:t></w:r>` +
    `<w:r><w:fldChar w:fldCharType="end"/></w:r>` +
    `<w:r><w:t xml:space="preserve"> ${escXml(label)}:  ${escXml(price)}</w:t></w:r>`
  );
};

const isCmfItem = (name) => {
  const n = (name || '').toLowerCase();
  return CMF_TIERS.some(t => n.includes(t));
};

const hasNonZeroAmount = (item) => {
  // Check years array (raw amounts before currency formatting)
  if (Array.isArray(item.years) && item.years.length > 0) {
    return item.years.some(y => parseFloat(y.amount || 0) !== 0);
  }
  // Fallback: check amounts map
  if (item.amounts && typeof item.amounts === 'object') {
    return Object.values(item.amounts).some(v => parseFloat(v || 0) !== 0);
  }
  return false;
};

export const formatTemplateData = async (data, currencyCode = 'IDR', proposalData = null, leadData = null) => {

  const formatted = { ...data };

  // ✅ ISCC SCOPE: Fetch ISCC scope mapping for dynamic scope formatting
  const isccScopeMapping = await fetchIsccScopeOptions();
  
  // ✅ TAMBAHAN: Ensure accreditation is always set (even if empty) - set early to prevent undefined
  // Handle both string and array formats for accreditation
  if (Array.isArray(data.accreditation)) {
    formatted.accreditation = data.accreditation.join(', '); // Join array elements with comma
  } else {
    formatted.accreditation = data.accreditation != null ? String(data.accreditation) : '';
  }

  // ✅ PROJECT NAME: Add project name field for template placeholders
  if (formatted.project_name !== undefined && formatted.project_name !== null) {
    formatted.project_name = String(formatted.project_name); // For {project_name} tag
  } else {
    formatted.project_name = ''; // Ensure it's always defined
  }

  // ✅ SCOPE: Add scope field for template placeholders with conditional parsing
  if (formatted.scope !== undefined && formatted.scope !== null) {
    // Check if the proposal template indicates ISCC/sustainability using proposalData
    let isIsccOrSustainability = false;

    if (proposalData && proposalData.proposalTemplateId) {
      // Try to determine from proposal data or fallback to scope format detection
      const proposalTemplateId = proposalData.proposalTemplateId;
      // For now, we'll use a simple heuristic: if scope is an array, JSON array string, or contains numeric IDs, treat as ISCC
      // In the future, this could be enhanced to check actual template names
      isIsccOrSustainability = Array.isArray(formatted.scope) ||
        (typeof formatted.scope === 'string' && formatted.scope.startsWith('[') && formatted.scope.endsWith(']')) ||
        (typeof formatted.scope === 'string' && /^\d+(,\s*\d+)*$/.test(formatted.scope.trim())); // Check for comma-separated numbers like "25, 27"
    } else {
      // Fallback: try to infer from scope data format
      isIsccOrSustainability = Array.isArray(formatted.scope) ||
        (typeof formatted.scope === 'string' && formatted.scope.startsWith('[') && formatted.scope.endsWith(']')) ||
        (typeof formatted.scope === 'string' && /^\d+(,\s*\d+)*$/.test(formatted.scope.trim())); // Check for comma-separated numbers
    }

    if (isIsccOrSustainability) {
      // For ISCC/Sustainability templates, convert scope array to readable format
      let scopeArray = [];
      if (Array.isArray(formatted.scope)) {
        scopeArray = formatted.scope;
      } else if (typeof formatted.scope === 'string' && formatted.scope.startsWith('[') && formatted.scope.endsWith(']')) {
        try {
          const parsed = JSON.parse(formatted.scope);
          scopeArray = Array.isArray(parsed) ? parsed : [formatted.scope];
        } catch (e) {
          scopeArray = [formatted.scope];
        }
      } else if (typeof formatted.scope === 'string' && formatted.scope.includes(',')) {
        // Handle comma-separated values like "25, 27"
        scopeArray = formatted.scope.split(',').map(s => s.trim()).filter(s => s);
      } else if (typeof formatted.scope === 'string' && /^\d+(,\s*\d+)*$/.test(formatted.scope.trim())) {
        // Handle comma-separated numbers like "25, 27"
        scopeArray = formatted.scope.split(',').map(s => s.trim()).filter(s => s);
      } else {
        scopeArray = [formatted.scope];
      }

      // Convert array of IDs/codes to readable labels using dynamic mapping
      const readableScopes = scopeArray
        .filter(item => item && item !== '')
        .map(item => {
          const key = String(item).toLowerCase();
          return isccScopeMapping[key] || `ISCC Scope ${item}`;
        });

      formatted.scope = readableScopes.join('\n');
    } else {
      // For non-ISCC/Sustainability templates: join array with newlines, keep strings as-is.
      // Each distinct per-standard scope occupies its own line.
      if (Array.isArray(formatted.scope)) {
        formatted.scope = formatted.scope.filter(s => s && String(s).trim()).join('\n');
      } else {
        formatted.scope = String(formatted.scope);
      }
    }
  } else {
    formatted.scope = ''; // Ensure it's always defined
  }

  // ✅ BANK: Add bank fields for template placeholders
  if (formatted.bank_name !== undefined && formatted.bank_name !== null) {
    formatted.bank_name = String(formatted.bank_name); // For {bank_name} tag
  } else {
    formatted.bank_name = ''; // Ensure it's always defined
  }

  if (formatted.bank_swift_code !== undefined && formatted.bank_swift_code !== null) {
    formatted.bank_swift_code = String(formatted.bank_swift_code); // For {bank_swift_code} tag
  } else {
    formatted.bank_swift_code = ''; // Ensure it's always defined
  }

  if (formatted.bank_currency !== undefined && formatted.bank_currency !== null) {
    formatted.bank_currency = String(formatted.bank_currency); // For {bank_currency} tag
  } else {
    formatted.bank_currency = ''; // Ensure it's always defined
  }

  // ✅ ISCC-SPECIFIC FIELDS: Add ISCC fields for template placeholders
  if (formatted.turnover !== undefined && formatted.turnover !== null) {
    formatted.turnover = String(formatted.turnover); // For {turnover} tag
  } else {
    formatted.turnover = ''; // Ensure it's always defined
  }

  if (formatted.facilities_and_address !== undefined && formatted.facilities_and_address !== null) {
    formatted.facilities_and_address = String(formatted.facilities_and_address); // For {facilities_and_address} tag
  } else {
    formatted.facilities_and_address = ''; // Ensure it's always defined
  }

  if (formatted.ghg_calculation !== undefined && formatted.ghg_calculation !== null) {
    formatted.ghg_calculation = String(formatted.ghg_calculation); // For {ghg_calculation} tag
  } else {
    formatted.ghg_calculation = ''; // Ensure it's always defined
  }

  // ✅ CARBON PROJECT & GHG FIELDS: Process for template replacement
  // Shared fields
  if (formatted.objective !== undefined && formatted.objective !== null) {
    formatted.objective = String(formatted.objective); // For {objective} tag
  } else {
    formatted.objective = ''; // Ensure it's always defined
  }

  if (formatted.sector !== undefined && formatted.sector !== null) {
    formatted.sector = String(formatted.sector); // For {sector} tag
  } else {
    formatted.sector = ''; // Ensure it's always defined
  }

  if (formatted.materiality !== undefined && formatted.materiality !== null) {
    formatted.materiality = String(formatted.materiality); // For {materiality} tag
  } else {
    formatted.materiality = ''; // Ensure it's always defined
  }

  if (formatted.number_of_sites !== undefined && formatted.number_of_sites !== null) {
    formatted.number_of_sites = String(formatted.number_of_sites); // For {number_of_sites} tag
  } else {
    formatted.number_of_sites = ''; // Ensure it's always defined
  }

  if (formatted.number_of_facilities !== undefined && formatted.number_of_facilities !== null) {
    formatted.number_of_facilities = String(formatted.number_of_facilities); // For {number_of_facilities} tag
  } else {
    formatted.number_of_facilities = ''; // Ensure it's always defined
  }

  if (formatted.site_address !== undefined && formatted.site_address !== null) {
    formatted.site_address = String(formatted.site_address); // For {site_address} tag
  } else {
    formatted.site_address = ''; // Ensure it's always defined
  }

  if (formatted.facility_address !== undefined && formatted.facility_address !== null) {
    formatted.facility_address = String(formatted.facility_address); // For {facility_address} tag
  } else {
    formatted.facility_address = ''; // Ensure it's always defined
  }

  // Carbon Project specific
  if (formatted.methodology !== undefined && formatted.methodology !== null) {
    formatted.methodology = String(formatted.methodology); // For {methodology} tag
  } else {
    formatted.methodology = ''; // Ensure it's always defined
  }

  if (formatted.type_of_emissions_removals !== undefined && formatted.type_of_emissions_removals !== null) {
    formatted.type_of_emissions_removals = String(formatted.type_of_emissions_removals); // For {type_of_emissions_removals} tag
  } else {
    formatted.type_of_emissions_removals = ''; // Ensure it's always defined
  }

  if (formatted.ghg_monitoring_period !== undefined && formatted.ghg_monitoring_period !== null) {
    formatted.ghg_monitoring_period = String(formatted.ghg_monitoring_period); // For {ghg_monitoring_period} tag
  } else {
    formatted.ghg_monitoring_period = ''; // Ensure it's always defined
  }

  if (formatted.ghg_emission_reductions !== undefined && formatted.ghg_emission_reductions !== null) {
    formatted.ghg_emission_reductions = String(formatted.ghg_emission_reductions); // For {ghg_emission_reductions} tag
  } else {
    formatted.ghg_emission_reductions = ''; // Ensure it's always defined
  }

  // GHG specific
  if (formatted.organization_activities !== undefined && formatted.organization_activities !== null) {
    formatted.organization_activities = String(formatted.organization_activities); // For {organization_activities} tag
  } else {
    formatted.organization_activities = ''; // Ensure it's always defined
  }

  if (formatted.reporting_boundaries !== undefined && formatted.reporting_boundaries !== null) {
    formatted.reporting_boundaries = String(formatted.reporting_boundaries); // For {reporting_boundaries} tag
  } else {
    formatted.reporting_boundaries = ''; // Ensure it's always defined
  }

  if (formatted.claim !== undefined && formatted.claim !== null) {
    formatted.claim = String(formatted.claim); // For {claim} tag
  } else {
    formatted.claim = ''; // Ensure it's always defined
  }

  if (formatted.level_of_assurance !== undefined && formatted.level_of_assurance !== null) {
    formatted.level_of_assurance = String(formatted.level_of_assurance); // For {level_of_assurance} tag
  } else {
    formatted.level_of_assurance = ''; // Ensure it's always defined
  }

  if (formatted.verification_location !== undefined && formatted.verification_location !== null) {
    formatted.verification_location = String(formatted.verification_location); // For {verification_location} tag
  } else {
    formatted.verification_location = ''; // Ensure it's always defined
  }

  // ✅ MATERIAL INPUTS/OUTPUTS: Add material fields for template placeholders
  if (formatted.material_input_names !== undefined && formatted.material_input_names !== null) {
    formatted.material_input_names = String(formatted.material_input_names); // For {material_input_names} tag
  } else {
    formatted.material_input_names = ''; // Ensure it's always defined
  }

  if (formatted.material_output_names !== undefined && formatted.material_output_names !== null) {
    formatted.material_output_names = String(formatted.material_output_names); // For {material_output_names} tag
  } else {
    formatted.material_output_names = ''; // Ensure it's always defined
  }

  // ✅ TAMBAHAN: Support uppercase placeholders for new fields
  formatted.PROJECT_NAME = formatted.project_name || '';
  formatted.SCOPE = formatted.scope || ''; // Already handled above with conditional parsing
  formatted.BANK_NAME = formatted.bank_name || '';
  formatted.BANK_SWIFT_CODE = formatted.bank_swift_code || '';
  formatted.BANK_CURRENCY = formatted.bank_currency || '';
  formatted.TURNOVER = formatted.turnover || '';
  formatted.FACILITIES_AND_ADDRESS = formatted.facilities_and_address || '';
  formatted.GHG_CALCULATION = formatted.ghg_calculation || '';

  // ✅ CARBON PROJECT & GHG FIELDS: Uppercase versions for template replacement
  formatted.OBJECTIVE = formatted.objective || '';
  formatted.SECTOR = formatted.sector || '';
  formatted.MATERIALITY = formatted.materiality || '';
  formatted.NUMBER_OF_SITES = formatted.number_of_sites || '';
  formatted.NUMBER_OF_FACILITIES = formatted.number_of_facilities || '';
  formatted.SITE_ADDRESS = formatted.site_address || '';
  formatted.FACILITY_ADDRESS = formatted.facility_address || '';
  formatted.METHODOLOGY = formatted.methodology || '';
  formatted.TYPE_OF_EMISSIONS_REMOVALS = formatted.type_of_emissions_removals || '';
  formatted.GHG_MONITORING_PERIOD = formatted.ghg_monitoring_period || '';
  formatted.GHG_EMISSION_REDUCTIONS = formatted.ghg_emission_reductions || '';
  formatted.ORGANIZATION_ACTIVITIES = formatted.organization_activities || '';
  formatted.REPORTING_BOUNDARIES = formatted.reporting_boundaries || '';
  formatted.CLAIM = formatted.claim || '';
  formatted.LEVEL_OF_ASSURANCE = formatted.level_of_assurance || '';
  formatted.VERIFICATION_LOCATION = formatted.verification_location || '';
  formatted.MATERIAL_INPUT_NAMES = formatted.material_input_names || '';
  formatted.MATERIAL_OUTPUT_NAMES = formatted.material_output_names || '';

  // ✅ TAMBAHAN: Support uppercase placeholder for accreditation
  formatted.ACCREDITATION = formatted.accreditation || '';

  // Format grand_total
  if (formatted.grand_total) {
    const amount = parseFloat(formatted.grand_total);
    formatted.grand_total = formatCurrency(amount, currencyCode);
  }
  
  // Tracks which CMF tier is selected — must be declared here (outer scope) so it's readable
  // after the service_items block closes.
  const selectedCmfTierRef = { basic: false, 'best practice': false, benchmark: false };

  // Format service_items amounts
  if (formatted.service_items && Array.isArray(formatted.service_items)) {

    // ✅ FIX: Build a map of standard_name -> version from items that have versions
    // Build map with cleaned standard names to match what we'll use later
    const standardVersionMap = {};
    formatted.service_items.forEach(item => {
      // Clean standard_name the same way we'll clean it later
      const originalStdName = item.standard_name || '';
      const cleanedStdName = removeTextAfterHash(originalStdName);
      const itemVersion = (item.version != null && item.version !== undefined) ? String(item.version).trim() : '';
      if (cleanedStdName && itemVersion && !standardVersionMap[cleanedStdName]) {
        standardVersionMap[cleanedStdName] = itemVersion;
      }
    });

    formatted.service_items = formatted.service_items.map((item, index) => {
      
      // Clean up service item names - remove text after '#'
      const cleanedItem = { ...item };
      
      // Apply removeTextAfterHash to common name fields
      if (cleanedItem.service_item_name) {
        const original = cleanedItem.service_item_name;
        cleanedItem.service_item_name = removeTextAfterHash(cleanedItem.service_item_name);
      }
      if (cleanedItem.name) {
        const original = cleanedItem.name;
        cleanedItem.name = removeTextAfterHash(cleanedItem.name);
      }
      if (cleanedItem.service_name) {
        const original = cleanedItem.service_name;
        cleanedItem.service_name = removeTextAfterHash(cleanedItem.service_name);
      }
      if (cleanedItem.standard_name) {
        const original = cleanedItem.standard_name;
        cleanedItem.standard_name = removeTextAfterHash(cleanedItem.standard_name);
      }
      if (cleanedItem.product_category_name) {
        const original = cleanedItem.product_category_name;
        cleanedItem.product_category_name = removeTextAfterHash(cleanedItem.product_category_name);
      }
      
      // ✅ FORMAT: Create standard_name_version if standard_name and version exist
      const standardName = cleanedItem.standard_name || '';
      // ✅ FIX: Use item's version, or inherit from standardVersionMap if item doesn't have one
      let version = (cleanedItem.version != null && cleanedItem.version !== undefined) ? String(cleanedItem.version).trim() : '';
      if (!version && standardName && standardVersionMap[standardName]) {
        version = standardVersionMap[standardName];
      }
      const standardNameWithVersion = version ? `${standardName}:${version}` : standardName;
      
      // ✅ CMF CHECKBOX: use named constants so unicode survives file encoding.
      const isCmf = isCmfItem(cleanedItem.service_item_name || cleanedItem.name);
      const cmfSelected = isCmf && hasNonZeroAmount(cleanedItem);
      const cmfCheck = isCmf ? (cmfSelected ? CMF_CHECKED : CMF_UNCHECKED) : '';

      // Track selected tier for top-level individual checkbox fields (before block transform renames items)
      if (cmfSelected) {
        const n = (cleanedItem.service_item_name || cleanedItem.name || '').toLowerCase();
        if (n.includes('best practice')) selectedCmfTierRef['best practice'] = true;
        else if (n.includes('benchmark'))    selectedCmfTierRef['benchmark']    = true;
        else if (n.includes('basic'))        selectedCmfTierRef['basic']        = true;
      }

      const formattedItem = {
        ...cleanedItem,
        version: version,
        standard_name_version: isCmf ? '' : standardNameWithVersion,
        cmf_check: cmfCheck,
        // {service_item_display} — plain text for first column
        service_item_display: isCmf ? '' : (standardNameWithVersion
          ? `${cleanedItem.service_item_name || cleanedItem.name}  -  ${standardNameWithVersion}`
          : (cleanedItem.service_item_name || cleanedItem.name || '')),
        participants: cleanedItem.participants !== undefined && cleanedItem.participants !== null
          ? String(cleanedItem.participants)
          : '',
        years: cleanedItem.years ? cleanedItem.years.map(year => {
          const formattedAmount = formatCurrency(year.amount, currencyCode);
          return {
            ...year,
            amount: formattedAmount,
            year: year.year
          };
        }) : []
      };
      return formattedItem;
    });

    // ✅ CMF BLOCK: Collapse all CMF tiers into a single multi-line field on the one CMF row.
    // The backend only returns the selected tier; we show all 3 with ☑/☐ in one cell
    // using \n line-breaks (docxtemplater is configured with linebreaks:true).
    const CMF_DISPLAY_TIERS = ['Basic', 'Best Practice', 'Benchmark'];

    formatted.service_items = formatted.service_items.map(item => {
      if (!isCmfItem(item.service_item_name)) return item;

      // Use only the first non-empty year amount — the CMF price is the same every year
      const selectedPrice = Array.isArray(item.years)
        ? (item.years.find(y => y.amount && y.amount !== '0') || item.years[0] || {}).amount || ''
        : '';

      const CMF_DEFAULT_PRICES = {
        'Basic':         formatCurrency(2000000, currencyCode),
        'Best Practice': formatCurrency(6000000, currencyCode),
        'Benchmark':     formatCurrency(12000000, currencyCode),
      };

      const tierLines = CMF_DISPLAY_TIERS.map(tier => {
        const isSelected = (item.service_item_name || '').toLowerCase().includes(tier.toLowerCase());
        const check = isSelected ? CMF_CHECKED : CMF_UNCHECKED;
        const price = isSelected && selectedPrice ? selectedPrice : CMF_DEFAULT_PRICES[tier];
        return `${check} ${tier}:  ${price}`;
      }).join('\n');
      const cmfBlock = `Certification Management Fee:\n${tierLines}`;

      return {
        ...item,
        service_item_name: 'Certification Management Fee',
        cmf_check: '',
        cmf_block: cmfBlock,
        standard_name_version: '',
        service_item_display: cmfBlock, // plain text — use {service_item_display} in template
      };
    });
  }

  // ✅ CMF INDIVIDUAL CHECKBOXES: top-level fields — use {cmf_basic_check} etc. anywhere in template.
  // Uses plain ASCII markers so postProcessDocxCheckboxes can target them precisely
  // without the wide-matching <w:r> regex that causes corruption in standalone table cells.
  formatted.cmf_basic_check        = selectedCmfTierRef['basic']         ? '##CMF_BASIC_CHK##'  : '##CMF_BASIC_UNCHK##';
  formatted.cmf_best_practice_check = selectedCmfTierRef['best practice'] ? '##CMF_BP_CHK##'    : '##CMF_BP_UNCHK##';
  formatted.cmf_benchmark_check     = selectedCmfTierRef['benchmark']     ? '##CMF_BENCH_CHK##' : '##CMF_BENCH_UNCHK##';

  // Format years year_total
  if (formatted.years && Array.isArray(formatted.years)) {
    formatted.years = formatted.years.map((year, index) => {
      const formattedTotal = formatCurrency(year.year_total, currencyCode);
      return {
        ...year,
        year_total: formattedTotal,
        year: year.year
      };
    });
  }
  
  // Add sales contact info
  if (formatted.sales_name) {
    formatted.sales_name = formatted.sales_name; // For {sales_name} tag
  }
  if (formatted.sales_phone) {
    formatted.sales_phone = formatted.sales_phone; // For {sales_phone} tag
  }
  if (formatted.sales_email) {
    formatted.sales_email = formatted.sales_email; // For {sales_email} tag
  }
  if (formatted.sales_position) {
    formatted.sales_position = formatted.sales_position; // For {sales_position} tag
  }
  
  // Add PIC name and position - fetch from lead contact instead of proposal
  formatted.pic_name = leadData?.pic || formatted.pic_name || ''; // For {pic_name} tag
  if (formatted.pic_position) {
    formatted.pic_position = formatted.pic_position; // For {pic_position} tag
  }
  
  // ✅ SIGNATURE NAME & POSITION: Add signature name and position for template placeholders
  if (formatted.signature_name) {
    formatted.signature_name = formatted.signature_name; // For {signature_name} tag
  } else {
    formatted.signature_name = ''; // Ensure it's always defined
  }
  if (formatted.signature_position) {
    formatted.signature_position = formatted.signature_position; // For {signature_position} tag
  } else {
    formatted.signature_position = ''; // Ensure it's always defined
  }
  
  // ✅ TAMBAHAN: Support uppercase placeholders for signature fields
  formatted.SIGNATURE_NAME = formatted.signature_name || '';
  formatted.SIGNATURE_POSITION = formatted.signature_position || '';
  
  // ✅ PARTICIPANTS: Add participants field for template placeholders (for training services)
  if (formatted.participants !== undefined && formatted.participants !== null) {
    formatted.participants = String(formatted.participants); // For {participants} tag
  } else {
    formatted.participants = ''; // Ensure it's always defined
  }
  // ✅ TAMBAHAN: Support uppercase placeholder for participants
  formatted.PARTICIPANTS = formatted.participants || '';
  
  // ✅ TRAINING DELIVERY METHOD: Add training delivery method for template placeholders
  if (formatted.training_delivery_method) {
    formatted.training_delivery_method = formatted.training_delivery_method; // For {training_delivery_method} tag
  } else {
    formatted.training_delivery_method = ''; // Ensure it's always defined
  }
  // ✅ TAMBAHAN: Support uppercase placeholder for training delivery method
  formatted.TRAINING_DELIVERY_METHOD = formatted.training_delivery_method || '';
  
  // Add signature image data for docxtemplater-image
  if (formatted.sales_signature_image || formatted.sales_signature) {
    formatted.sales_signature = formatted.sales_signature_image || formatted.sales_signature; // For {%sales_signature} tag
  }
  if (formatted.pic_signature_image || formatted.pic_signature) {
    formatted.pic_signature = formatted.pic_signature_image || formatted.pic_signature; // For {%pic_signature} tag
  }
  
  // ✅ NEW: Format hierarchical services structure (Service -> Standard -> Service Items)
  if (formatted.services && Array.isArray(formatted.services)) {
    
    formatted.services = formatted.services.map((service, serviceIndex) => {
      
      return {
        ...service,
        standards: service.standards && Array.isArray(service.standards) 
          ? service.standards.map((standard, standardIndex) => {
              
              const standardName = standard.standard_name || '';
              const standardVersion = (standard.version != null && standard.version !== undefined) ? String(standard.version).trim() : '';
              // ✅ FORMAT: Add standard name with version (e.g., "ISO 27001:2019")
              const standardNameWithVersion = standardVersion ? `${standardName}:${standardVersion}` : standardName;
              
              return {
                ...standard,
                standard_name: standardName,
                standard_id: standard.standard_id || '',
                version: standardVersion, // ✅ TAMBAHAN: Include version (ensure string, never undefined)
                standard_name_version: standardNameWithVersion, // ✅ FORMAT: Standard name with version formatted (e.g., "ISO 27001:2019")
                service_items: standard.service_items && Array.isArray(standard.service_items)
                  ? (() => {
                      // ✅ FIX: Find version from any service item if standard version is empty
                      let effectiveVersion = standardVersion;
                      if (!effectiveVersion || effectiveVersion.trim() === '') {
                        // Try to get version from first service item that has one
                        for (const item of standard.service_items) {
                          const itemVersion = (item.version != null && item.version !== undefined) ? String(item.version).trim() : '';
                          if (itemVersion) {
                            effectiveVersion = itemVersion;
                            break;
                          }
                        }
                      }
                      // Recalculate standardNameWithVersion with effective version
                      const effectiveStandardNameWithVersion = effectiveVersion ? `${standardName}:${effectiveVersion}` : standardName;
                      
                      return standard.service_items.map((item, itemIndex) => {
                        
                        // Clean up service item name
                        const cleanedItemName = removeTextAfterHash(item.service_item_name);
                        
                        // Use item's version if available, otherwise use effective version from standard/first item
                        const itemVersion = (item.version != null && item.version !== undefined) ? String(item.version).trim() : '';
                        const finalVersion = itemVersion || effectiveVersion;
                        const finalStandardNameWithVersion = finalVersion ? `${standardName}:${finalVersion}` : standardName;
                        
                        // Format year amounts with currency
                        const formattedYears = item.years ? item.years.map(year => {
                          const formattedAmount = formatCurrency(year.amount, currencyCode);
                          return {
                            year: year.year,
                            amount: formattedAmount
                          };
                        }) : [];
                        
                        const nestedIsCmf = isCmfItem(cleanedItemName || item.service_item_name);
                        const nestedCmfCheck = nestedIsCmf
                          ? (hasNonZeroAmount(item) ? CMF_CHECKED : CMF_UNCHECKED)
                          : '';

                        return {
                          ...item,
                          service_item_name: cleanedItemName,
                          version: finalVersion,
                          standard_name_version: nestedIsCmf ? '' : finalStandardNameWithVersion,
                          cmf_check: nestedCmfCheck,
                          participants: item.participants !== undefined && item.participants !== null
                            ? String(item.participants)
                            : '',
                          years: formattedYears
                        };
                      });
                    })()
                  : []
              };
            })
          : []
      };
    });
  }
  
  // ✅ TAMBAHAN: Ensure accreditation is included (already set above, but ensure it's not overwritten)
  if (formatted.accreditation === undefined || formatted.accreditation === null) {
    if (Array.isArray(data.accreditation)) {
      formatted.accreditation = data.accreditation.join(', '); // Join array elements with comma
    } else {
      formatted.accreditation = data.accreditation != null ? String(data.accreditation) : '';
    }
  }
  
  // ✅ TAMBAHAN: Ensure version is included in standards array
  if (formatted.standards && Array.isArray(formatted.standards)) {
    formatted.standards = formatted.standards.map(standard => {
      const name = standard.name || '';
      const version = (standard.version != null && standard.version !== undefined) ? String(standard.version).trim() : '';
      const scheme = standard.standard_scheme || '';
      // ✅ FORMAT: Add name with version and scheme (e.g., "ISO 27001:2019 (ISMS)" or "ISO 27001 (ISMS)")
      let nameWithVersion = version ? `${name}:${version}` : name;
      if (scheme.trim() !== '') {
        nameWithVersion += ` ${scheme}`;
      }

      return {
        ...standard,
        name: name,
        standard_scheme: scheme,
        version: version, // ✅ TAMBAHAN: Include version (ensure string, never undefined)
        name_version: nameWithVersion // ✅ FORMAT: Name with version and scheme formatted
      };
    });
  }
  
  // ✅ TAMBAHAN: Format commercial terms for template
  if (data.commercialTerms) {
    const commercialTerms = data.commercialTerms;
    formatted.commercialTerms = commercialTerms;
    
    // Format for docxtemplater loops
    if (commercialTerms.sections && Array.isArray(commercialTerms.sections)) {
      formatted.commercial_term_sections = commercialTerms.sections.map(section => ({
        section_title: section.section_title || '',
        section_type: section.section_type || '',
        points: section.points || []
      }));
      
      // ✅ PERBAIKAN: Create named properties for specific sections (for direct access)
      // This allows {clause_1_title}, {clause_1_points}, etc.
      commercialTerms.sections.forEach((section, index) => {
        const clauseNum = index + 1;
        formatted[`clause_${clauseNum}_title`] = section.section_title || '';
        formatted[`clause_${clauseNum}_points`] = section.points || [];
        
        // Also support section_type based access (e.g., GENERAL, PAYMENT_TERMS, etc.)
        // This requires backend to include section_type in the response
        if (section.section_type) {
          formatted[`section_${section.section_type}_title`] = section.section_title || '';
          formatted[`section_${section.section_type}_points`] = section.points || [];
          
          // ✅ ADD: Create individual point properties for direct access (e.g., section_TRAVEL_EXPENSES_point_0)
          if (section.points && Array.isArray(section.points)) {
            section.points.forEach((point, pointIndex) => {
              formatted[`section_${section.section_type}_point_${pointIndex}`] = point;
            });
            
            // Also create convenience properties for first, second, third points
            if (section.points.length > 0) {
              formatted[`section_${section.section_type}_first_point`] = section.points[0];
            }
            if (section.points.length > 1) {
              formatted[`section_${section.section_type}_second_point`] = section.points[1];
            }
            if (section.points.length > 2) {
              formatted[`section_${section.section_type}_third_point`] = section.points[2];
            }
          }
        }
      });
      
      
    } 
    
    if (commercialTerms.standalone_points && Array.isArray(commercialTerms.standalone_points)) {
      formatted.commercial_term_points = commercialTerms.standalone_points;
    } else {
      formatted.commercial_term_points = [];
    }
  } else {
    console.warn('No commercialTerms found in data');
    formatted.commercial_term_sections = [];
    formatted.commercial_term_points = [];
  }
  
  return formatted;
};
